/**
 * Header - Navigation and controls with dropdown menu
 */
import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Heart, Moon, Sun, Languages, Shapes, Package, Calculator, Users, ChevronDown, GitCompare, BookOpen, Sparkles, Bug } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import BugReportModal from '../BugReportModal';
import { AdsterraBanner } from '../ads/AdsterraBanner';

export function Header() {
  const { theme, toggleTheme, language, toggleLanguage, t, favorites } = useApp();
  const location = useLocation();
  const [toolsOpen, setToolsOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setToolsOpen(false);
  }, [location.pathname]);
  
  const mainNavItems = [
    { path: '/', icon: Home, label: t('home') },
    { path: '/dexbuilder', icon: Users, label: 'DexBuilder' },
    { path: '/favorites', icon: Heart, label: t('favorites'), badge: favorites.length }
  ];

  const toolsItems = [
    { path: '/types', icon: Shapes, label: language === 'es' ? 'Tipos' : 'Types', desc: language === 'es' ? 'Tabla de efectividad' : 'Type chart' },
    { path: '/items', icon: Package, label: language === 'es' ? 'Objetos' : 'Items', desc: language === 'es' ? 'Base de datos' : 'Database' },
    { path: '/nature', icon: Sparkles, label: language === 'es' ? 'Naturalezas' : 'Natures', desc: language === 'es' ? 'Guía de naturalezas' : 'Nature guide' },
    { path: '/calculator', icon: Calculator, label: language === 'es' ? 'Calculadora' : 'Calculator', desc: language === 'es' ? 'Daño de combate' : 'Battle damage' },
    { path: '/comparator', icon: GitCompare, label: language === 'es' ? 'Comparador' : 'Comparator', desc: language === 'es' ? 'Comparar Pokémon' : 'Compare Pokémon' }
  ];

  const isToolsActive = toolsItems.some(item => location.pathname === item.path);
  
  return (
    <header className="sticky top-0 z-50 w-full" data-testid="header">
      <div className="bg-card/90 backdrop-blur-xl border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                className="w-10 h-10 overflow-hidden"
              >
                <img 
                  src="/logo-dx.png" 
                  alt="DexRiddle" 
                  className="w-full h-full object-contain"
                />
              </motion.div>
            </Link>

            {/* Ad banner - desktop only, between logo and nav */}
            <AdsterraBanner
              adKey="d7128a1440ec2fc3e548081f0a2fd44a"
              width={468}
              height={60}
              className="hidden xl:block"
            />

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              {/* Main nav items */}
              {mainNavItems.map(({ path, icon: Icon, label, badge }) => {
                const isActive = location.pathname === path;
                return (
                  <Link key={path} to={path}>
                    <Button
                      variant={isActive ? "default" : "ghost"}
                      size="sm"
                      data-testid={`nav-${path.replace('/', '') || 'home'}`}
                      className={`relative ${
                        isActive 
                          ? 'bg-gradient-to-r from-violet-600 to-rose-500 text-white hover:from-violet-700 hover:to-rose-600' 
                          : ''
                      }`}
                    >
                      <Icon className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">{label}</span>
                      {badge > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs rounded-full flex items-center justify-center">
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </Button>
                  </Link>
                );
              })}

              {/* Tools Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <Button
                  variant={isToolsActive ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setToolsOpen(!toolsOpen)}
                  data-testid="nav-tools"
                  className={cn(
                    "gap-1",
                    isToolsActive && 'bg-gradient-to-r from-violet-600 to-rose-500 text-white hover:from-violet-700 hover:to-rose-600'
                  )}
                >
                  <BookOpen className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{language === 'es' ? 'Herramientas' : 'Tools'}</span>
                  <ChevronDown className={cn("w-3 h-3 transition-transform", toolsOpen && "rotate-180")} />
                </Button>

                <AnimatePresence>
                  {toolsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50"
                    >
                      <div className="py-1">
                        {toolsItems.map(({ path, icon: Icon, label, desc }) => {
                          const isActive = location.pathname === path;
                          return (
                            <Link
                              key={path}
                              to={path}
                              className={cn(
                                "flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition-colors",
                                isActive && "bg-violet-500/10"
                              )}
                            >
                              <div className={cn(
                                "w-8 h-8 rounded-lg flex items-center justify-center",
                                isActive ? "bg-violet-500 text-white" : "bg-muted"
                              )}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div>
                                <div className={cn("font-medium text-sm", isActive && "text-violet-600 dark:text-violet-400")}>
                                  {label}
                                </div>
                                <div className="text-xs text-muted-foreground">{desc}</div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>
            
            {/* Controls */}
            <div className="flex items-center gap-1">
              {/* Language toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleLanguage}
                data-testid="language-toggle"
                className="h-9 w-9"
              >
                <Languages className="w-4 h-4" />
                <span className="sr-only">Toggle language</span>
              </Button>

              {/* Theme toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
                className="h-9 w-9"
              >
                {theme === 'light' ? (
                  <Moon className="w-4 h-4" />
                ) : (
                  <Sun className="w-4 h-4" />
                )}
                <span className="sr-only">Toggle theme</span>
              </Button>

              {/* Bug Report Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setBugReportOpen(true)}
                data-testid="bug-report-btn"
                className="h-9 w-9"
                title={language === 'es' ? 'Reportar error' : 'Report bug'}
              >
                <Bug className="w-4 h-4" />
                <span className="sr-only">Report bug</span>
              </Button>
            </div>

            {/* Bug Report Modal */}
            <BugReportModal isOpen={bugReportOpen} onClose={() => setBugReportOpen(false)} />
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
